import { z } from "zod";
import { day, optional } from "./appointments";
import { recordId } from "./contracts";
import { unset } from "./fields";
import { savedPaymentMethodType } from "./payer-profiles";

const named = z.object({ id: recordId, name: z.string() });

export const paymentDueScope = z.enum(["appointments", "workDays", "both"]);
export type PaymentDueScope = z.infer<typeof paymentDueScope>;

export const paymentDueListQuery = z.object({
  actingOrganization: optional(recordId.toLowerCase()),
  from: optional(day),
  to: optional(day),
  scope: z.preprocess(unset, paymentDueScope.default("both")),
});
export type PaymentDueListQuery = z.infer<typeof paymentDueListQuery>;

export const paymentDueItemQuery = paymentDueListQuery.extend({ payer: recordId.toLowerCase() });
export type PaymentDueItemQuery = z.infer<typeof paymentDueItemQuery>;

export const paymentRecipientKind = z.enum(["subcontractor", "worker"]);
export type PaymentRecipientKind = z.infer<typeof paymentRecipientKind>;

export const paymentRecipient = named.extend({ kind: paymentRecipientKind });
export type PaymentRecipient = z.infer<typeof paymentRecipient>;

export const dueCount = z.object({ count: z.number().int().nonnegative(), amount: z.number() });
export type DueCount = z.infer<typeof dueCount>;

export const dueTally = z.object({ amount: z.number(), appointments: dueCount, workDays: dueCount });
export type DueTally = z.infer<typeof dueTally>;

export const paymentDue = z.object({
  payer: named,
  recipient: paymentRecipient,
  paymentTermsDays: z.number().int(),
  readyForPayouts: z.boolean().nullable(),
  owed: dueTally,
  completed: dueTally,
});
export type PaymentDue = z.infer<typeof paymentDue>;

export const paymentDueList = z.object({ items: z.array(paymentDue) });
export type PaymentDueList = z.infer<typeof paymentDueList>;

export const duePaymentStatus = z.enum(["pending", "partial", "paid"]);
export type DuePaymentStatus = z.infer<typeof duePaymentStatus>;

export const paymentDueItem = z.object({
  kind: z.enum(["appointment", "workDay"]),
  id: recordId,
  contract: recordId.nullable(),
  property: named.nullable(),
  unit: z.string().nullable(),
  date: z.string().nullable(),
  amount: z.number(),
  hoursWorked: z.number().nullable(),
  status: duePaymentStatus,
});
export type PaymentDueItem = z.infer<typeof paymentDueItem>;

export const outstandingDueItems = z.object({ items: z.array(paymentDueItem), amount: z.number() });
export type OutstandingDueItems = z.infer<typeof outstandingDueItems>;

export const dueItemReport = z.object({ items: z.array(paymentDueItem), amount: z.number(), paid: z.number(), unpaid: z.number() });
export type DueItemReport = z.infer<typeof dueItemReport>;

export const markPaidRequest = z
  .object({
    payer: recordId.toLowerCase(),
    actingOrganization: optional(recordId.toLowerCase()),
    from: optional(day),
    to: optional(day),
    scope: paymentDueScope.default("both"),
  })
  .strict();
export type MarkPaidRequest = Omit<z.infer<typeof markPaidRequest>, "scope"> & { scope?: PaymentDueScope };

export const markedPaid = z.object({ payout: recordId.nullable() });
export type MarkedPaid = z.infer<typeof markedPaid>;

export const PAYMENT_TERMS_DAYS_MIN = 1;
export const PAYMENT_TERMS_DAYS_MAX = 90;

export const paymentTermsRequest = z
  .object({
    payer: recordId.toLowerCase(),
    actingOrganization: optional(recordId.toLowerCase()),
    days: z.number().int().min(PAYMENT_TERMS_DAYS_MIN).max(PAYMENT_TERMS_DAYS_MAX),
  })
  .strict();
export type PaymentTermsRequest = z.infer<typeof paymentTermsRequest>;

export const paymentTerms = z.object({ paymentTermsDays: z.number().int() });
export type PaymentTerms = z.infer<typeof paymentTerms>;

export const paymentKind = z.enum(["due", "extra"]);
export type PaymentKind = z.infer<typeof paymentKind>;

export const EXTRA_PAYMENT_DESCRIPTION_MAX_LENGTH = 500;

const paymentParties = {
  payer: recordId.toLowerCase(),
  actingOrganization: optional(recordId.toLowerCase()),
  kind: paymentKind.default("due"),
  from: optional(day),
  to: optional(day),
  scope: paymentDueScope.default("both"),
  description: optional(z.string().trim().min(1).max(EXTRA_PAYMENT_DESCRIPTION_MAX_LENGTH)),
};

const describesExtra = (request: { kind: PaymentKind; description?: string }) => request.kind === "due" || request.description !== undefined;

const EXTRA_NEEDS_DESCRIPTION = { message: "Describe what the extra payment is for.", path: ["description"] };

export const paymentSheetRequest = z
  .object({ ...paymentParties, amountCents: z.number().int().positive(), paymentMethodType: savedPaymentMethodType })
  .strict()
  .refine(describesExtra, EXTRA_NEEDS_DESCRIPTION);
export type PaymentSheetRequest = z.input<typeof paymentSheetRequest>;

export const paymentSheet = z.object({
  paymentIntent: z.string(),
  clientSecret: z.string().nullable(),
  amountCents: z.number().int(),
  currency: z.string(),
  status: z.string(),
  customer: z.string().nullable(),
  ephemeralKey: z.string().nullable(),
  applicationFeeCents: z.number().int(),
  netTransferCents: z.number().int(),
  paymentMethodType: savedPaymentMethodType,
  attempt: recordId,
});
export type PaymentSheet = z.infer<typeof paymentSheet>;

export const finalizePaymentRequest = z
  .object({ ...paymentParties, paymentIntent: z.string().trim().min(1).max(255), amountCents: optional(z.number().int().positive()) })
  .strict();
export type FinalizePaymentRequest = z.input<typeof finalizePaymentRequest>;

export const finalizedPaymentStatus = z.enum(["pending", "paid", "failed"]);
export type FinalizedPaymentStatus = z.infer<typeof finalizedPaymentStatus>;

export const finalizedPayment = z.object({ payout: recordId.nullable(), status: finalizedPaymentStatus });
export type FinalizedPayment = z.infer<typeof finalizedPayment>;

export const savedMethodPaymentRequest = z
  .object({
    payer: recordId.toLowerCase(),
    actingOrganization: optional(recordId.toLowerCase()),
    from: optional(day),
    to: optional(day),
    scope: paymentDueScope.default("both"),
    amountCents: z.number().int().positive(),
  })
  .strict();
export type SavedMethodPaymentRequest = z.input<typeof savedMethodPaymentRequest>;

export const savedMethodPayment = z.object({
  paymentIntent: z.string(),
  status: z.string(),
  requiresAction: z.boolean(),
  clientSecret: z.string().nullable(),
  amountCents: z.number().int(),
  currency: z.string(),
  applicationFeeCents: z.number().int(),
  netTransferCents: z.number().int(),
  paymentMethodType: savedPaymentMethodType,
  attempt: recordId,
});
export type SavedMethodPayment = z.infer<typeof savedMethodPayment>;
